import type { DirectoryUser, DirectoryUsersLoadRequest } from '@jarvis/platform-ui';
import { apiFetch, readJson, readErrorMessage } from './client';
import { listClientPortalTenants, type ClientPortalTenantOption } from './clientPortalStorageMountsAdmin';

export type { ClientPortalTenantOption, DirectoryUser, DirectoryUsersLoadRequest };

export interface DirectoryUserListPage {
  total: number;
  items: DirectoryUser[];
}

export interface CreatedClientUser {
  id: string;
  displayName: string;
  userPrincipalName: string;
  mail: string | null;
  accountEnabled: boolean;
  tempPassword: string;
  department: string | null;
  jobTitle: string | null;
  company: string | null;
  givenName?: string | null;
  surname?: string | null;
  employeeType?: string | null;
  employeeHireDate?: string | null;
  officeLocation?: string | null;
  businessPhone?: string | null;
  licenseWarning?: string | null;
}

export interface VerifiedDomain {
  id: string;
  isDefault: boolean;
  isVerified: boolean;
}

export interface UserGroupMembership {
  groupId: string;
  displayName: string;
  isMember: boolean;
  assignedSprRoles: string[];
}

function tenantBase(tenantId: string): string {
  return `/api/admin/client-accounts/tenants/${encodeURIComponent(tenantId)}`;
}

function tenantUsersBase(tenantId: string): string {
  return `${tenantBase(tenantId)}/users`;
}

function toQueryString(params: Record<string, string | number | boolean | undefined>): string {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return parts.length === 0 ? '' : `?${parts.join('&')}`;
}

export { listClientPortalTenants };

export async function listClientAccountsUsers(
  tenantId: string,
  opts: DirectoryUsersLoadRequest = {}
): Promise<DirectoryUserListPage> {
  const res = await apiFetch(
    `${tenantUsersBase(tenantId)}${toQueryString(opts as Record<string, string | number | boolean | undefined>)}`
  );
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<DirectoryUserListPage>(res);
}

export async function listClientAccountsDomains(tenantId: string): Promise<VerifiedDomain[]> {
  const res = await apiFetch(`${tenantBase(tenantId)}/domains`);
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<VerifiedDomain[]>(res);
}

export async function createClientAccountsUser(
  tenantId: string,
  req: {
    givenName: string;
    surname: string;
    userPrincipalName: string;
    mailNickname: string;
    tempPassword?: string | null;
    mail?: string | null;
    department?: string | null;
    jobTitle?: string | null;
    company?: string | null;
    employeeType?: string | null;
    employeeHireDate?: string | null;
    officeLocation?: string | null;
    businessPhone?: string | null;
  }
): Promise<CreatedClientUser> {
  const res = await apiFetch(tenantUsersBase(tenantId), {
    method: 'POST',
    body: JSON.stringify(req)
  });
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<CreatedClientUser>(res);
}

export interface InvitedGuestUser {
  id: string;
  displayName: string;
  userPrincipalName: string;
  mail: string | null;
  accountEnabled: boolean;
  inviteRedeemUrl: string | null;
  department: string | null;
  jobTitle: string | null;
  company: string | null;
  membershipWarning: string | null;
}

export async function inviteClientAccountsGuestUser(
  tenantId: string,
  req: {
    givenName: string;
    surname: string;
    email: string;
    department?: string | null;
    jobTitle?: string | null;
    company?: string | null;
    groupIds?: string[];
  }
): Promise<InvitedGuestUser> {
  const res = await apiFetch(`${tenantUsersBase(tenantId)}/guest-invites`, {
    method: 'POST',
    body: JSON.stringify(req)
  });
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<InvitedGuestUser>(res);
}

export async function updateClientAccountsUser(
  tenantId: string,
  userId: string,
  req: {
    givenName?: string | null;
    surname?: string | null;
    accountEnabled?: boolean | null;
    mail?: string | null;
    department?: string | null;
    jobTitle?: string | null;
    company?: string | null;
    employeeType?: string | null;
    employeeHireDate?: string | null;
    updateEmployeeHireDate?: boolean;
    officeLocation?: string | null;
    businessPhone?: string | null;
  }
): Promise<DirectoryUser> {
  const res = await apiFetch(`${tenantUsersBase(tenantId)}/${encodeURIComponent(userId)}`, {
    method: 'PATCH',
    body: JSON.stringify(req)
  });
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<DirectoryUser>(res);
}

export async function deleteClientAccountsUser(tenantId: string, userId: string): Promise<void> {
  const res = await apiFetch(`${tenantUsersBase(tenantId)}/${encodeURIComponent(userId)}`, {
    method: 'DELETE'
  });
  if (!res.ok) throw new Error(await readErrorMessage(res));
}

export interface ClientUserLicenseActionResult {
  success: boolean;
  warning: string | null;
}

export async function assignClientAccountsUserP2(
  tenantId: string,
  userId: string
): Promise<ClientUserLicenseActionResult> {
  const res = await apiFetch(
    `${tenantUsersBase(tenantId)}/${encodeURIComponent(userId)}/assign-p2`,
    { method: 'POST' }
  );
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<ClientUserLicenseActionResult>(res);
}

export async function removeClientAccountsUserP2(
  tenantId: string,
  userId: string
): Promise<ClientUserLicenseActionResult> {
  const res = await apiFetch(
    `${tenantUsersBase(tenantId)}/${encodeURIComponent(userId)}/remove-p2`,
    { method: 'POST' }
  );
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<ClientUserLicenseActionResult>(res);
}

export interface ClientUserOffboardResult {
  userId: string;
  accountDisabled: boolean;
  groupsRemovedCount: number;
  groupsRemainingWarning: string[];
  sessionsRevoked: boolean;
  licenseRemoved: boolean;
  licenseWarning: string | null;
  emailSent: boolean;
  emailWarning: string | null;
}

export async function offboardClientAccountsUser(
  tenantId: string,
  userId: string
): Promise<ClientUserOffboardResult> {
  const res = await apiFetch(
    `${tenantUsersBase(tenantId)}/${encodeURIComponent(userId)}/offboard`,
    { method: 'POST' }
  );
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<ClientUserOffboardResult>(res);
}

export async function getUserGroups(tenantId: string, userId: string): Promise<UserGroupMembership[]> {
  const res = await apiFetch(`${tenantUsersBase(tenantId)}/${encodeURIComponent(userId)}/groups`);
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<UserGroupMembership[]>(res);
}

export async function setUserGroups(
  tenantId: string,
  userId: string,
  groupIds: string[]
): Promise<string[]> {
  const res = await apiFetch(`${tenantUsersBase(tenantId)}/${encodeURIComponent(userId)}/groups`, {
    method: 'PUT',
    body: JSON.stringify({ groupIds })
  });
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<string[]>(res);
}
